<template>
  <DialogContainer
    :showDialog="showDialog"
    title="Add Equipment"
    icon="add"
    :toolbarActions="[
      {
        text: 'Save',
        icon: 'save',
        buttonColor: 'success',
        emitEvent: 'onSubmitClicked',
        showIsLoading: true,
        isDisabledFunc: () => !isFormValid,
      },
      {
        text: 'Cancel',
        depressed: true,
        emitEvent: 'onCancelClicked',
      },
    ]"
    @onCancelClicked="onCancelClicked"
    @onSubmitClicked="onSubmitClicked"
    :isLoading="isLoading"
  >
    <v-container pa-0 grid-list-lg>
      <v-form v-model="isFormValid" ref="form">
        <v-layout wrap>
          <v-flex xs12>
            <ControlSlider
              label="Count"
              v-model="count"
              :max="10"
              :min="1"
              :disabled="isLoading"
            />
          </v-flex>
          <v-flex xs12>
            <v-layout row wrap>
              <v-flex
                xs12
                :class="{ md6: count > 1 }"
                v-for="(key, index) in count"
                :key="key"
              >
                <ControlTextField
                  :label="key + ') Equipment Serial/Id*'"
                  v-model="assetNames[index]"
                  :disabled="isLoading"
                  :rules="[(v) => !!v || 'Required']"
                />
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex xs12 md6>
            <ControlAutoComplete
              :items="enabledLocationList"
              v-model="locationId"
              item-text="name"
              item-value="id"
              label="Location*"
              :disabled="isLoading"
              :rules="[(v) => !!v || 'Required']"
            />
          </v-flex>
          <v-flex xs12 md6>
            <ControlAutoComplete
              :items="enabledClientList"
              v-model="clientId"
              item-text="name"
              item-value="id"
              label="Owner*"
              :disabled="isLoading"
              :rules="[(v) => !!v || 'Required']"
            />
          </v-flex>
          <v-flex xs12 md6>
            <ControlAutoComplete
              :items="enabledCategoryList"
              autocomplete
              item-text="name"
              item-value="id"
              v-model="categoryId"
              clearable
              @change="modelId = null"
              label="Category*"
              :disabled="isLoading"
              :rules="[(v) => !!v || 'Required']"
            />
          </v-flex>
          <v-flex xs12 md6>
            <ControlAutoComplete
              :items="enabledManufacturerList"
              autocomplete
              item-text="name"
              item-value="id"
              v-model="manufacturerId"
              clearable
              @change="modelId = null"
              label="Manufacturer*"
              :disabled="isLoading"
              :rules="[(v) => !!v || 'Required']"
            />
          </v-flex>
          <v-flex xs12>
            <ControlAutoComplete
              :items="modelListByCategoryAndManufacturer"
              v-model="modelId"
              item-text="name"
              item-value="id"
              :label="modelLabel"
              :disabled="
                !categoryId ||
                !manufacturerId ||
                !modelListByCategoryAndManufacturer.length ||
                isLoading
              "
              :rules="[(v) => !!v || 'Required']"
            />
          </v-flex>
          <v-flex xs12>
            <ControlTextArea
              v-model="comments"
              label="Comments"
              :disabled="isLoading"
            />
          </v-flex>
          <!-- <v-flex xs12>
          <ControlCheckbox
            v-model="addAssetActionAfterCreate"
            label="Add activity on new equipment after creation?"
            :disabled="isLoading"
          />
        </v-flex> -->
        </v-layout>
        <small>*indicates required field</small>
      </v-form>
    </v-container>
  </DialogContainer>
</template>

<script>
import mixinAssetHelper from '../../../common/mixins/mixinAssetHelper'

import DialogContainer from '../../../common/components/layouts/DialogContainer'
import ControlTextField from '../../../common/components/controls/ControlTextField'
import ControlAutoComplete from '../../../common/components/controls/ControlAutoComplete'
import ControlCheckbox from '../../../common/components/controls/ControlCheckbox'
import ControlTextArea from '../../../common/components/controls/ControlTextArea'
import ControlSlider from '../../../common/components/controls/ControlSlider'

export default {
  components: {
    DialogContainer,
    ControlTextField,
    ControlAutoComplete,
    ControlCheckbox,
    ControlTextArea,
    ControlSlider,
  },
  mixins: [mixinAssetHelper],
  props: {
    showDialog: {
      type: Boolean,
      required: true,
    },
    modelList: {
      type: Array,
      required: true,
    },
    manufacturerList: {
      type: Array,
      required: true,
    },
    categoryList: {
      type: Array,
      required: true,
    },
    locationList: {
      type: Array,
      required: true,
    },
    clientList: {
      type: Array,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    isFormValid: false,
    assetNames: [],
    categoryId: null,
    locationId: null,
    clientId: null,
    manufacturerId: null,
    modelId: null,
    comments: null,
    addAssetActionAfterCreate: null,
    count: 1,
  }),
  computed: {
    modelLabel() {
      return this.categoryId &&
        this.manufacturerId &&
        !this.modelListByCategoryAndManufacturer.length
        ? `No models found for ${this._entityPropertyValueByName({
          entityList: this.categoryList,
          idKey: 'id',
          idValue: this.categoryId,
          propertyKey: 'name',
        })} and ${this._entityPropertyValueByName({
          entityList: this.manufacturerList,
          idKey: 'id',
          idValue: this.manufacturerId,
          propertyKey: 'name',
        })}`
        : 'Model*'
    },
    enabledLocationList() {
      return this.locationList.filter((location) => location.isEnabled)
    },
    enabledUserList() {
      return this.userList.filter((user) => user.isEnabled)
    },
    enabledClientList() {
      return this.clientList.filter((client) => client.isEnabled)
    },
    enabledCategoryList() {
      return this.categoryList.filter((category) => category.isEnabled)
    },
    enabledManufacturerList() {
      return this.manufacturerList.filter(
        (manufacturer) => manufacturer.isEnabled
      )
    },
    modelListByCategoryAndManufacturer() {
      return this._modelsByCategoryIdAndManufacturerId({
        modelList: this.modelList,
        categoryId: this.categoryId,
        manufacturerId: this.manufacturerId,
      })
    },
  },
  showDialog() {
    if (this.showDialog && this.selectedAssets) {
      this.assetIds = this.selectedAssets.map((asset) => asset.id)
    }
    if (!this.showDialog) {
      this.clearDialog()
    }
  },
  watch: {
    count() {
      if (this.count < this.assetNames.length) {
        this.assetNames.splice(this.count)
      }
    },
    showDialog() {
      if (!this.showDialog) {
        this.clearDialog()
      }
    },
    modelListByCategoryAndManufacturer() {
      if (
        !this.modelListByCategoryAndManufacturer.some(
          (model) => model.id === this.modelId
        )
      ) {
        this.modelId = null
      }
    },
  },
  methods: {
    onSubmitClicked() {
      this.$emit('onSubmitClicked', {
        assetNames: this.assetNames,
        categoryId: this.categoryId,
        locationId: this.locationId,
        manufacturerId: this.manufacturerId,
        modelId: this.modelId,
        comments: this.comments,
        clientId: this.clientId,
        addAssetActionAfterCreate: this.addAssetActionAfterCreate,
      })
    },
    onCancelClicked() {
      this.$emit('onCancelClicked')
    },
    clearDialog() {
      this.$refs.form.reset()
      this.assetNames = []
      this.categoryId = null
      this.locationId = null
      this.clientId = null
      this.manufacturerId = null
      this.modelId = null
      this.comments = null
      this.clientOwnerId = null
      this.count = 1
      this.addAssetActionAfterCreate = null
    },
  },
}
</script>